// LA SUPERFICIE FINANZAS SOBRE UNA SOLA VERDAD (2026-10-04 · FINANCE-SURFACE-RECONCILIATION).
//
// Funciones puras: cada cifra de Finanzas sale de aquí con su contrato escrito. Nada se clasifica por nombre ni por id.
//
//   VENTA      = proyecto REAL (ni demo ni QA) de un cliente REAL. Un cobro NO es una venta; un checkout tampoco.
//   PAGADO     = cobro REAL pagado, atribuido a su venta por proyecto_id o por la propuesta (quote_id) que la originó.
//   RECOVER    = valor de la venta − pagos reales atribuidos (nunca negativo; el exceso se marca SOBREPAGO).
//   EMITIDO    = cobro REAL pedido y aún exigible. Cancelado, fallido, expirado o abandonado NO es exigible.
//   GASTO      = egreso EJECUTADO (pagado). Una línea de presupuesto importado no es un gasto.
//   STUDIO     = suscripción del estudio. PERSONAL nunca entra en un KPI del estudio, la pague quien la pague.
//   DESCONOCIDO nunca es 0: se devuelve null y la UI lo dice.
import { soloOperacionReal, normText } from '../../../lib/operationalData';

const r2 = (x) => Math.round((Number(x) || 0) * 100) / 100;
const num = (x) => {
  if (x === null || x === undefined || x === '') return null;
  const v = Number(x);
  return Number.isFinite(v) ? v : null;
};

// ── PERIODOS ────────────────────────────────────────────────────────────────
export const PERIODO_MES = 'MES_ACTUAL';
export const PERIODO_ANIO = 'ANIO_ACTUAL';
export const PERIODO_TODO = 'TODO';
export const PERIODOS = [
  { id: PERIODO_MES, label: 'Mes actual' },
  { id: PERIODO_ANIO, label: 'Año en curso' },
  { id: PERIODO_TODO, label: 'Todo el historial' },
];

export function etiquetaPeriodo(periodo, hoy = new Date()) {
  if (periodo === PERIODO_MES) return `Mes actual · ${hoy.toLocaleDateString('es-EC', { month: 'long', year: 'numeric' })}`;
  if (periodo === PERIODO_ANIO) return `Año en curso · ${hoy.getFullYear()}`;
  return 'Todo el historial';
}

export function enPeriodo(fecha, periodo, hoy = new Date()) {
  if (periodo === PERIODO_TODO) return true;
  if (!fecha) return false;
  const d = new Date(String(fecha).length <= 10 ? `${String(fecha).slice(0, 10)}T12:00:00` : fecha);
  if (Number.isNaN(d.getTime())) return false;
  if (d.getFullYear() !== hoy.getFullYear()) return false;
  return periodo === PERIODO_ANIO ? true : d.getMonth() === hoy.getMonth();
}

// ── COBROS ──────────────────────────────────────────────────────────────────
const NO_EXIGIBLES = ['cancelled', 'canceled', 'cancelado', 'cancelada', 'failed', 'fallido', 'expired', 'expirado',
  'abandoned', 'abandonado', 'refunded', 'reembolsado', 'void', 'anulado'];

export function cobroPagado(c = {}) {
  return normText(c.status) === 'paid' || normText(c.transferencia_estado) === 'transferencia_aprobada';
}

export function cobroExigible(c = {}) {
  if (cobroPagado(c)) return false;
  // Una transferencia rechazada sigue siendo exigible: el cliente debe y se espera un comprobante nuevo.
  return !NO_EXIGIBLES.includes(normText(c.status));
}

export function fechaDePago(c = {}) {
  return c.paid_at || c.fecha_pago || c.transferencia_validada_at || c.updated_at || c.created_at || null;
}

export function metodoDeCobro(c = {}) {
  const raw = normText(`${c.payment_source || ''} ${c.paid_via || ''} ${c.via || ''} ${c.transferencia_estado || ''}`);
  if (raw.includes('transfer') || c.comprobante_path || c.comprobante_url) return 'transfer';
  if (raw.includes('stripe') || raw.includes('tarjeta') || raw.includes('checkout') || c.stripe_session_id || c.checkout_session_id || c.stripe_id) return 'stripe';
  return 'unknown';
}

function etapaDeCobro(c = {}) {
  const raw = normText(`${c.payment_stage || ''} ${c.tipo || ''}`);
  if (raw.includes('application_extra') || raw.includes('extra')) return 'application_extra';
  if (raw.includes('final_40') || raw.includes('40%') || raw.includes('saldo')) return 'final_40';
  if (raw.includes('pago_total') || raw.includes('pago total')) return 'pago_total';
  if (raw.includes('anticipo') || raw.includes('60%')) return 'anticipo_60';
  return 'unknown';
}

/** Resumen de Cobros SOLO sobre operación real. `periodo` acota los pagados por su fecha de pago. */
export function resumenCobros(cobros = [], { periodo = PERIODO_TODO, hoy = new Date() } = {}) {
  const reales = soloOperacionReal(cobros);
  const pagados = reales.filter(c => cobroPagado(c) && enPeriodo(fechaDePago(c), periodo, hoy));
  const exigibles = reales.filter(cobroExigible);
  const suma = rows => r2(rows.reduce((s, c) => s + (Number(c.monto) || 0), 0));
  const porValidar = exigibles.filter(c => /transferencia_en_revision|comprobante_enviado/.test(normText(`${c.status} ${c.transferencia_estado}`)));
  const vencidos = exigibles.filter(c => ['overdue', 'vencido'].includes(normText(c.status)));
  return {
    excluidosNoReales: (Array.isArray(cobros) ? cobros.length : 0) - reales.length,
    pagado: suma(pagados), nPagados: pagados.length,
    emitidosPendientes: suma(exigibles), nEmitidosPendientes: exigibles.length,
    porValidar: suma(porValidar), nPorValidar: porValidar.length,
    vencidos: suma(vencidos), nVencidos: vencidos.length,
    stripeCobrado: suma(pagados.filter(c => metodoDeCobro(c) === 'stripe')),
    nStripe: pagados.filter(c => metodoDeCobro(c) === 'stripe').length,
    transferenciaCobrada: suma(pagados.filter(c => metodoDeCobro(c) === 'transfer')),
    nTransferencia: pagados.filter(c => metodoDeCobro(c) === 'transfer').length,
    porEtapa: ['anticipo_60', 'final_40', 'pago_total', 'application_extra'].reduce((acc, k) => {
      const rows = pagados.filter(c => etapaDeCobro(c) === k);
      acc[k] = { monto: suma(rows), n: rows.length };
      return acc;
    }, {}),
  };
}

// ── VENTAS Y RECOVER ────────────────────────────────────────────────────────
const PROYECTO_CERRADO_SIN_VENTA = /cancel|anulad|archivad|descartad/;

function clienteReal(cliente) {
  return Boolean(cliente) && cliente.is_test !== true && cliente.is_demo !== true && (cliente.clase_de_origen === undefined || cliente.clase_de_origen === 'REAL');
}

/**
 * Ventas reales con su pago atribuido y su saldo. `clientes` debe traer los flags (`is_test`, `is_demo`): un proyecto
 * cuyo cliente es de prueba —o no se puede verificar— NO es una venta real, aunque su propia bandera diga REAL
 * (la 397 materializó el modo del proyecto; la herencia del cliente se aplica aquí, en la lectura).
 */
export function ventasReales({ proyectos = [], clientes = [], cobros = [] } = {}) {
  const clientesById = new Map((clientes || []).map(c => [String(c.id), c]));
  const pagosReales = soloOperacionReal(cobros).filter(cobroPagado);
  const usados = new Set();
  const ventas = [];
  const excluidas = [];
  soloOperacionReal(proyectos).forEach(p => {
    if (p.is_test === true || p.is_demo === true) return;
    const clienteId = String(p.clienteId || p.cliente_id || '');
    const cliente = clientesById.get(clienteId);
    const valor = num(p.package_price ?? p.precioPaquete ?? p.precio ?? p.monto);
    const estado = normText(`${p.estado || ''} ${p.production_status || ''}`);
    if (p.is_internal === true) { excluidas.push({ id: p.id, motivo: 'PROYECTO_INTERNO' }); return; }
    if (!clienteReal(cliente)) { excluidas.push({ id: p.id, motivo: 'CLIENTE_NO_REAL_O_NO_VERIFICABLE' }); return; }
    if (PROYECTO_CERRADO_SIN_VENTA.test(estado)) { excluidas.push({ id: p.id, motivo: 'PROYECTO_CANCELADO' }); return; }
    if (valor === null || valor <= 0) { excluidas.push({ id: p.id, motivo: 'SIN_VALOR' }); return; }
    const atribuidos = pagosReales.filter(c => !usados.has(String(c.id)) && (
      (c.proyecto_id && String(c.proyecto_id) === String(p.id))
      || (!c.proyecto_id && c.quote_id && p.quote_id && String(c.quote_id) === String(p.quote_id))
    ));
    atribuidos.forEach(c => usados.add(String(c.id)));
    const pagado = r2(atribuidos.reduce((s, c) => s + (Number(c.monto) || 0), 0));
    ventas.push({
      id: `venta-${p.id}`, proyectoId: p.id, clienteId, cliente: cliente?.nombre || 'Cliente',
      concepto: p.package_name || p.paquete || p.nombre || 'Proyecto', fecha: p.created_at || p.fecha || null,
      monto: r2(valor), pagado, saldo: r2(Math.max(0, valor - pagado)), sobrepago: r2(Math.max(0, pagado - valor)),
      cobros: atribuidos.map(c => c.id), estado: pagado >= valor ? 'paid' : (pagado > 0 ? 'partial' : 'pending'),
    });
  });
  return { ventas, excluidas, pagosSinVenta: pagosReales.filter(c => !usados.has(String(c.id))).map(c => c.id) };
}

export function resumenVentas(ventas = [], { periodo = PERIODO_TODO, hoy = new Date() } = {}) {
  const enP = ventas.filter(v => enPeriodo(v.fecha, periodo, hoy));
  return {
    total: r2(enP.reduce((s, v) => s + v.monto, 0)),
    pagado: r2(enP.reduce((s, v) => s + Math.min(v.pagado, v.monto), 0)),
    saldo: r2(enP.reduce((s, v) => s + v.saldo, 0)),
    n: enP.length,
  };
}

/** RECOVER: saldo contractual por cobrar de ventas reales. Independiente del periodo: una deuda viva no caduca. */
export function recoverReal(ventas = []) {
  const conSaldo = ventas.filter(v => v.saldo > 0);
  return { total: r2(conSaldo.reduce((s, v) => s + v.saldo, 0)), ventas: conSaldo };
}

// ── SUSCRIPCIONES ───────────────────────────────────────────────────────────
export const STUDIO = 'STUDIO';
export const PERSONAL = 'PERSONAL';

/** El ámbito lo declara la fila (categoría «Personal»). El pagador (LLC USA / Ecuador) NO es el ámbito. */
export function ambitoSuscripcion(row = {}) {
  return normText(row.categoria) === 'personal' || normText(row.ambito) === 'personal' ? PERSONAL : STUDIO;
}

export function equivalenteMensual(row = {}) {
  const eq = num(row.costo_mensual_equiv);
  if (eq !== null) return eq;
  const costo = num(row.costo_usd) || 0;
  return r2(row.ciclo === 'anual' ? costo / 12 : costo);
}

function diasHasta(fecha, hoy) {
  if (!fecha) return null;
  const d = new Date(`${String(fecha).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  const h = new Date(hoy); h.setHours(0, 0, 0, 0);
  return Math.ceil((d - h) / 86400000);
}

export function resumenSuscripciones(rows = [], hoy = new Date()) {
  const activas = rows.filter(r => r.estado === 'activo');
  const studio = activas.filter(r => ambitoSuscripcion(r) === STUDIO);
  const personal = activas.filter(r => ambitoSuscripcion(r) === PERSONAL);
  const suma = xs => r2(xs.reduce((s, r) => s + equivalenteMensual(r), 0));
  const renov = xs => xs.filter(r => { const d = diasHasta(r.fecha_renovacion, hoy); return d !== null && d >= 0 && d <= 30; });
  return {
    studioMensual: suma(studio.filter(r => r.ciclo !== 'anual')),
    studioAnualEquivalente: suma(studio.filter(r => r.ciclo === 'anual')),
    studioTotalEquivalente: suma(studio),
    studioPagadoDesdeUsa: suma(studio.filter(r => r.entidad_pagadora === 'llc_usa')),
    studioPagadoDesdeEcuador: suma(studio.filter(r => r.entidad_pagadora !== 'llc_usa')),
    personalMensual: suma(personal),
    personal: personal.map(r => r.herramienta),
    activasStudio: studio.length,
    activasPersonal: personal.length,
    renovacionesStudio30: renov(studio),
    renovacionesPersonal30: renov(personal),
    sinFechaStudio: studio.filter(r => !r.fecha_renovacion).map(r => r.herramienta),
  };
}

// ── GASTOS FIJOS ────────────────────────────────────────────────────────────
export const FIJO_ACTIVO = 'ACTIVO';
export const FIJO_MONTO_NO_DEFINIDO = 'MONTO_NO_DEFINIDO';
export const FIJO_INACTIVO = 'INACTIVO';

/** Una fila sin monto que no está activa no vale $0: su monto actual no está definido (variable / desconocido). */
export function estadoGastoFijo(row = {}) {
  const monto = num(row.monto_mensual);
  if (row.activo !== false && monto !== null && monto > 0) return FIJO_ACTIVO;
  if (monto === null || monto === 0) return FIJO_MONTO_NO_DEFINIDO;
  return FIJO_INACTIVO;
}

export function costoFijoMensualConocido(gastosFijos = []) {
  const activos = gastosFijos.filter(r => estadoGastoFijo(r) === FIJO_ACTIVO);
  return { monto: r2(activos.reduce((s, r) => s + (num(r.monto_mensual) || 0), 0)),
           noDefinidos: gastosFijos.filter(r => estadoGastoFijo(r) === FIJO_MONTO_NO_DEFINIDO).map(r => r.concepto) };
}

// ── GASTOS ──────────────────────────────────────────────────────────────────
/** Ejecutado = pagado. `recurrente` (y cualquier otro estado) es una línea de presupuesto, no un gasto. */
export function esGastoEjecutado(g = {}) {
  return ['paid', 'pagado', 'pagada'].includes(normText(g.status));
}

export function separarGastos(gastos = []) {
  const ejecutados = gastos.filter(esGastoEjecutado);
  const presupuesto = gastos.filter(g => !esGastoEjecutado(g));
  return { ejecutados, presupuesto,
           totalEjecutado: r2(ejecutados.reduce((s, g) => s + (Number(g.monto) || 0), 0)),
           totalPresupuesto: r2(presupuesto.reduce((s, g) => s + (Number(g.monto) || 0), 0)) };
}

/** Costo operativo mensual conocido del estudio para la salud: fijos activos + suscripciones STUDIO (equivalente). */
export function costoOperativoMensualConocido({ gastosFijos = [], suscripciones = [] } = {}, hoy = new Date()) {
  const f = costoFijoMensualConocido(gastosFijos);
  const s = resumenSuscripciones(suscripciones, hoy);
  return { monto: r2(f.monto + s.studioTotalEquivalente), fijos: f.monto, suscripcionesStudio: s.studioTotalEquivalente, noDefinidos: f.noDefinidos };
}
